import { useCallback, useMemo, useState } from "react";

import { useCurrentUserContext } from "@/features/users/hooks/useCurrentUserContext";
import { useSearchUsersApi } from "@/features/users/hooks/useUsers";
import type { User, UserSearchResponse } from "@/features/users/schemas/user";

export interface UsersPageFilters {
  search: string;
  role: string;
  sortBy: "active_imaging" | "created_at";
}

export interface UsersPageState {
  filters: UsersPageFilters;
  setFilters: (filters: Partial<UsersPageFilters>) => void;
  users: User[];
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
  setRefreshKey: (key: string) => void;
  error: string | null;
  currentUser: User | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  debouncedSearchTerm: string;
  // Paginación real del API
  pagination: UserSearchResponse["pagination"] | null;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const ROLE_MAP: Record<string, User["type"]> = {
  Admin: "admin",
  Médico: "physician",
  Paciente: "patient",
  Institución: "institution",
};

export function useUsersPage(): UsersPageState {
  const { currentUserData } = useCurrentUserContext();
  const [filters, setFilters] = useState<UsersPageFilters>({
    search: "",
    role: "Todos los roles",
    sortBy: "active_imaging",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [refreshKey, setRefreshKey] = useState("");

  const selectedType =
    filters.role !== "Todos los roles" ? ROLE_MAP[filters.role] : undefined;

  // q="" devuelve todos los usuarios en la carga inicial.
  // includeUserData trae el documento completo de Firestore (incluye last_login).
  const { data, isLoading, isFetching, error, refetch } = useSearchUsersApi({
    q: searchTerm,
    ...(selectedType && { type: selectedType }),
    includeUserData: true,
    hitsPerPage: pageSize,
    page: currentPage,
    sortByActiveImaging: filters.sortBy === "active_imaging" ? true : undefined,
    sortByCreatedAt: filters.sortBy === "created_at" ? true : undefined,
    ...(refreshKey && { refresh: refreshKey }),
  });

  const users = useMemo((): User[] => (data?.data ?? []) as unknown as User[], [data]);

  const updateFilters = useCallback((newFilters: Partial<UsersPageFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset a página 1 al cambiar filtros
  }, []);

  const updateSearchTerm = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset a página 1 al buscar
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  return {
    filters,
    setFilters: updateFilters,
    users,
    isLoading,
    isFetching,
    refetch,
    setRefreshKey,
    error: error?.message ?? null,
    currentUser: currentUserData,
    searchTerm,
    setSearchTerm: updateSearchTerm,
    debouncedSearchTerm: searchTerm,
    pagination: data?.pagination ?? null,
    currentPage,
    pageSize,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
  };
}
